import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, Type } from 'lucide-react';
import { useDocStore, type ThemeName } from '../store/docStore';

interface ThemeOption {
  name: ThemeName;
  label: string;
  /** Sample-text font stack for the live preview (mirrors the CSS tokens). */
  font: string;
}

const SANS_FONT =
  "'Inter Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const SERIF_FONT =
  "'Source Serif 4 Variable', 'Charter', 'Iowan Old Style', Georgia, serif";

const GROUPS: { heading: string; options: ThemeOption[] }[] = [
  {
    heading: 'Sans',
    options: [
      { name: 'sans-contrast', label: 'High contrast', font: SANS_FONT },
      { name: 'sans-reader', label: 'Reader', font: SANS_FONT },
    ],
  },
  {
    heading: 'Serif',
    options: [
      { name: 'serif-contrast', label: 'High contrast', font: SERIF_FONT },
      { name: 'serif-reader', label: 'Reader', font: SERIF_FONT },
    ],
  },
];

// Flattened option order for arrow-key navigation across both groups.
const FLAT: ThemeOption[] = GROUPS.flatMap((g) => g.options);

/**
 * Typography theme switcher. An icon button (lucide `Type`) that opens a
 * popover listing the four themes ({Sans, Serif} × {High contrast, Reader})
 * grouped under Sans / Serif headers. Each option is a LIVE preview — a sample
 * heading + line of prose rendered in that theme's font, plus a palette swatch —
 * with a check on the active theme. Selecting sets the persisted store `theme`.
 *
 * Fully keyboard accessible (ArrowUp/Down move, Enter/Space select, Esc closes,
 * Home/End jump), dismisses on click-outside / blur, and never auto-opens (so
 * the App tests / jsdom stay quiet). Styled natively to match the chrome.
 */
export function ThemeSwitcher(): ReactElement {
  const theme = useDocStore((s) => s.theme);
  const setTheme = useDocStore((s) => s.setTheme);
  const [open, setOpen] = useState(false);
  // Index of the option focused by keyboard while the popover is open.
  const [activeIndex, setActiveIndex] = useState(0);
  // Viewport coordinates for the portaled menu, anchored under the trigger.
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const close = useCallback((focusButton: boolean) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  const openMenu = useCallback(() => {
    const idx = FLAT.findIndex((o) => o.name === theme);
    setActiveIndex(idx >= 0 ? idx : 0);
    setOpen(true);
  }, [theme]);

  // Anchor the portaled menu under the trigger (right-aligned, like the
  // in-flow popover was). Measured before paint so there's no jump.
  useLayoutEffect(() => {
    if (!open) return;
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuPos({
      top: rect.bottom + 6,
      right: Math.max(0, (window.innerWidth || 0) - rect.right),
    });
  }, [open]);

  // Dismiss on outside pointer / Escape / window blur — only while open. The
  // menu is portaled to <body>, so treat clicks inside it as inside too.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onWindowBlur = (): void => setOpen(false);
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [open]);

  // Move DOM focus to the active option whenever it changes while open.
  useEffect(() => {
    if (!open) return;
    itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const select = useCallback(
    (name: ThemeName) => {
      setTheme(name);
      close(true);
    },
    [setTheme, close],
  );

  const onMenuKeyDown = useCallback(
    (e: ReactKeyboardEvent): void => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % FLAT.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((i) => (i - 1 + FLAT.length) % FLAT.length);
          break;
        case 'Home':
          e.preventDefault();
          setActiveIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setActiveIndex(FLAT.length - 1);
          break;
        case 'Escape':
          e.preventDefault();
          close(true);
          break;
        case 'Tab':
          // Let focus leave the popover naturally; close it.
          setOpen(false);
          break;
        default:
          break;
      }
    },
    [close],
  );

  const onButtonKeyDown = useCallback(
    (e: ReactKeyboardEvent): void => {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (open) close(false);
        else openMenu();
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        close(true);
      }
    },
    [open, openMenu, close],
  );

  let flatIndex = -1;

  return (
    <div className="mp-theme-switcher" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="mp-iconbtn mp-theme-switcher__trigger"
        aria-label="Typography theme"
        title="Typography theme"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onButtonKeyDown}
      >
        <Type size={15} aria-hidden strokeWidth={2} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="mp-theme-menu"
            id={menuId}
            role="menu"
            aria-label="Typography theme"
            aria-orientation="vertical"
            style={{ top: menuPos.top, right: menuPos.right }}
            onKeyDown={onMenuKeyDown}
          >
          {GROUPS.map((group) => (
            <div className="mp-theme-menu__group" key={group.heading}>
              <div className="mp-theme-menu__heading" role="presentation">
                {group.heading}
              </div>
              {group.options.map((opt) => {
                flatIndex += 1;
                const index = flatIndex;
                const selected = opt.name === theme;
                return (
                  <button
                    key={opt.name}
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    tabIndex={index === activeIndex ? 0 : -1}
                    className="mp-theme-menu__item"
                    data-theme-name={opt.name}
                    data-active={index === activeIndex ? '' : undefined}
                    onClick={() => select(opt.name)}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    {/* LIVE preview: a sample heading + prose line rendered in
                        this theme's prose font, with a palette swatch. The
                        `data-theme-name` on a scoped subtree makes the swatch +
                        sample pick up the theme's palette tokens (resolved under
                        the CURRENT light/dark mode). */}
                    <span
                      className="mp-theme-swatch"
                      aria-hidden
                      data-theme-name={opt.name}
                    >
                      <span className="mp-theme-swatch__bg" />
                      <span className="mp-theme-swatch__accent" />
                    </span>
                    <span
                      className="mp-theme-sample"
                      data-theme-name={opt.name}
                      style={{ '--mp-sample-font': opt.font } as PreviewStyle}
                    >
                      <span className="mp-theme-sample__heading">{group.heading}</span>
                      <span className="mp-theme-sample__prose">{opt.label}</span>
                    </span>
                    <span className="mp-theme-menu__check" aria-hidden>
                      {selected && <Check size={14} strokeWidth={2.5} />}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

// Allow the CSS custom property on the inline style object under TS strict.
type PreviewStyle = CSSProperties & { '--mp-sample-font': string };
